/**
 * KanbanBoard - Top-level container for the Trello-style kanban board.
 * @implements FS-005.9.1.1 - Column Structure
 * @implements FS-005.9.1.2 - Board Container
 * @implements FS-005.9.3.1 - Modal Trigger (card click opens detail modal)
 * @implements BS-005.9.1 - Read-Only Board
 * @implements BS-005.9.2 - Flat Card Display
 *
 * Renders seven columns in horizontal layout:
 * backlog | consolidation | todo | inProgress | review | qa | done
 *
 * The board is read-only; ticket movement is handled by agents and the
 * Scrum Master engine (FS-005.10). Users click cards to view details in a modal.
 */

import { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { COLUMN_ORDER } from '@/utils/kanban';
import { KanbanColumn } from './KanbanColumn';
import { TicketDetailModal } from './TicketDetailModal';
import type { KanbanTicketPayload } from '@/stores/KanbanStore';
import type { ValidationStatus } from './ValidationStatusBadge';

/**
 * Props for KanbanBoard component.
 */
export interface KanbanBoardProps {
  /**
   * Project ID for the board.
   * Will be used to connect to KanbanStore in US-117.
   */
  projectId: string;
  /**
   * All tickets on the board. Will be populated from KanbanStore in US-117.
   * For now, defaults to empty array.
   */
  tickets?: KanbanTicketPayload[];
  /**
   * Tickets grouped by column. Will be populated from KanbanStore in US-117.
   * For now, defaults to empty record.
   */
  ticketsByColumn?: Record<string, KanbanTicketPayload[]>;
  /**
   * Validation statuses by ticket ID. Will be populated from KanbanStore in US-117.
   * Used to show validation status in the detail modal.
   */
  validationStatuses?: Record<string, string>;
}

/**
 * KanbanBoard - Kanban board container with seven columns.
 *
 * @implements FS-005.9.1.1 - Column Structure
 * @implements FS-005.9.1.2 - Board Container
 * @implements FS-005.9.3.1 - Modal Trigger (card click opens detail modal)
 *
 * Layout:
 * - CSS Grid with 7 equal-width columns
 * - Board fills available viewport width
 * - Each column scrolls vertically independently
 * - Horizontal scroll if viewport is too narrow
 *
 * Modal behavior:
 * - Card click opens TicketDetailModal
 * - Child click in modal updates modal to show child ticket
 * - Close via backdrop click, escape key, or X button
 */
export const KanbanBoard = observer(
  ({
    projectId: _projectId,
    tickets = [],
    ticketsByColumn = {},
    validationStatuses = {},
  }: KanbanBoardProps) => {
    // TODO (US-117): Connect to KanbanStore via useScrumMaster hook using _projectId
    // For now, use the tickets prop passed from parent

    // State for selected ticket modal
    const [selectedTicketId, setSelectedTicketId] = useState<string | null>(
      null,
    );

    // Find the selected ticket from the tickets array
    const selectedTicket = selectedTicketId
      ? tickets.find((t) => t.id === selectedTicketId) || null
      : null;

    // Get validation status for selected ticket
    const selectedValidationStatus = selectedTicketId
      ? (validationStatuses[selectedTicketId] as ValidationStatus | undefined)
      : undefined;

    // Handle card click - open modal
    const handleCardClick = useCallback((ticketId: string) => {
      setSelectedTicketId(ticketId);
    }, []);

    // Handle modal close
    const handleCloseModal = useCallback(() => {
      setSelectedTicketId(null);
    }, []);

    // Handle child click in modal - navigate to child ticket
    const handleChildClick = useCallback(
      (childId: string) => {
        // Check if child ticket exists in our tickets array
        const childTicket = tickets.find((t) => t.id === childId);
        if (childTicket) {
          setSelectedTicketId(childId);
        }
      },
      [tickets],
    );

    return (
      <Box
        sx={{
          // Fill available space below header
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Column container with horizontal layout */}
        <Box
          sx={{
            display: 'grid',
            // 7 equal-width columns
            gridTemplateColumns: 'repeat(7, minmax(200px, 1fr))',
            gap: 1.5,
            flex: 1,
            overflow: 'auto', // Horizontal scroll if viewport too narrow
            px: 2,
            py: 1.5,
          }}
          data-testid="kanban-board"
        >
          {COLUMN_ORDER.map((column) => (
            <KanbanColumn
              key={column}
              column={column}
              tickets={ticketsByColumn[column] || []}
              onCardClick={handleCardClick}
            />
          ))}
        </Box>

        {/* Ticket Detail Modal */}
        <TicketDetailModal
          ticket={selectedTicket}
          open={selectedTicketId !== null}
          onClose={handleCloseModal}
          onChildClick={handleChildClick}
          validationStatus={selectedValidationStatus}
        />
      </Box>
    );
  },
);

// Display name for React DevTools
KanbanBoard.displayName = 'KanbanBoard';
